import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Menu,
  MenuList,
  MenuItem as MuiMenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Pagination,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { format, parseISO, isWithinInterval, subDays, subMonths } from 'date-fns';
import { tr } from 'date-fns/locale';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const FilterCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(2),
}));

const EmissionChip = styled(Chip)(({ theme, severity }) => ({
  fontWeight: 'bold',
  '& .MuiChip-icon': {
    color: 'inherit',
  },
}));

const ITEMS_PER_PAGE = 10;

const TourismCalculationHistory = ({ 
  calculations, 
  onLoad, 
  onDelete, 
  onAddToComparison, 
  onExport 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [filterSeason, setFilterSeason] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  
  const [selectedCalculations, setSelectedCalculations] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeCalculation, setActiveCalculation] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, calculation: null });

  // Filtrelenmiş ve sıralanmış hesaplamaları hesapla
  const filteredCalculations = useMemo(() => {
    let filtered = [...calculations];

    // Arama filtresi
    if (searchTerm) {
      filtered = filtered.filter(calc => 
        calc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        calc.hotelName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Periyot filtresi
    if (filterPeriod !== 'all') {
      filtered = filtered.filter(calc => calc.period === filterPeriod);
    }

    // Mevsim filtresi
    if (filterSeason !== 'all') {
      filtered = filtered.filter(calc => calc.season === filterSeason);
    }

    // Tarih aralığı filtresi
    if (dateRange !== 'all') {
      const now = new Date();
      let startDate;
      
      switch (dateRange) {
        case 'week':
          startDate = subDays(now, 7);
          break;
        case 'month':
          startDate = subMonths(now, 1);
          break;
        case 'quarter':
          startDate = subMonths(now, 3);
          break;
        case 'year':
          startDate = subMonths(now, 12);
          break;
        default:
          startDate = null;
      }

      if (startDate) {
        filtered = filtered.filter(calc => 
          isWithinInterval(parseISO(calc.timestamp), { start: startDate, end: now })
        );
      }
    }

    // Sıralama
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        break;
      case 'highest':
        filtered.sort((a, b) => b.total - a.total);
        break;
      case 'lowest':
        filtered.sort((a, b) => a.total - b.total);
        break;
      case 'title':
        filtered.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        break;
      default:
        break;
    }

    return filtered;
  }, [calculations, searchTerm, filterPeriod, filterSeason, dateRange, sortBy]);

  // Sayfalama
  const paginatedCalculations = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredCalculations.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredCalculations, currentPage]);

  const totalPages = Math.ceil(filteredCalculations.length / ITEMS_PER_PAGE);

  const handleMenuOpen = (event, calculation) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setActiveCalculation(calculation);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveCalculation(null);
  };

  const handleCalculationSelect = (calculationId) => {
    setSelectedCalculations(prev => 
      prev.includes(calculationId)
        ? prev.filter(id => id !== calculationId)
        : [...prev, calculationId]
    );
  };

  const handleBulkAction = (action) => {
    switch (action) {
      case 'export':
        onExport(selectedCalculations, 'csv');
        break;
      case 'compare':
        selectedCalculations.forEach(id => {
          const calculation = calculations.find(calc => calc.id === id);
          if (calculation) onAddToComparison(calculation);
        });
        break;
      case 'delete':
        selectedCalculations.forEach(id => onDelete(id));
        setSelectedCalculations([]);
        break;
      default:
        break;
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteDialog.calculation) {
      onDelete(deleteDialog.calculation.id);
      setDeleteDialog({ open: false, calculation: null });
    }
  };

  const getEmissionSeverity = (total) => {
    if (total > 100) return 'error';
    if (total > 50) return 'warning';
    return 'success';
  };

  const getEmissionLabel = (total) => {
    if (total > 100) return 'Yüksek';
    if (total > 50) return 'Orta';
    return 'Düşük';
  };

  const getPeriodLabel = (period) => {
    const labels = { daily: 'Günlük', monthly: 'Aylık', yearly: 'Yıllık' };
    return labels[period] || period;
  };

  const getSeasonLabel = (season) => {
    const labels = { 
      spring: 'İlkbahar', 
      summer: 'Yaz', 
      autumn: 'Sonbahar', 
      winter: 'Kış' 
    };
    return labels[season] || season;
  };

  if (calculations.length === 0) {
    return (
      <Alert severity="info">
        <Typography variant="h6">📈 Henüz hesaplama yapılmamış</Typography>
        <Typography variant="body2">
          İlk karbon ayak izi hesaplamanızı yapmak için "Yeni Hesaplama" sekmesine geçin.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Filtre ve Arama Bölümü */}
      <FilterCard>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            🔍 Arama ve Filtreler
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Başlık veya otel adı ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      🔍
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Periyot</InputLabel>
                <Select
                  value={filterPeriod}
                  onChange={(e) => setFilterPeriod(e.target.value)}
                  label="Periyot"
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="daily">Günlük</MenuItem>
                  <MenuItem value="monthly">Aylık</MenuItem>
                  <MenuItem value="yearly">Yıllık</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Mevsim</InputLabel>
                <Select
                  value={filterSeason}
                  onChange={(e) => setFilterSeason(e.target.value)}
                  label="Mevsim"
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="spring">İlkbahar</MenuItem>
                  <MenuItem value="summer">Yaz</MenuItem>
                  <MenuItem value="autumn">Sonbahar</MenuItem>
                  <MenuItem value="winter">Kış</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tarih</InputLabel>
                <Select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  label="Tarih"
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="week">Son Hafta</MenuItem>
                  <MenuItem value="month">Son Ay</MenuItem>
                  <MenuItem value="quarter">Son 3 Ay</MenuItem>
                  <MenuItem value="year">Son Yıl</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sırala</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sırala"
                >
                  <MenuItem value="newest">En Yeni</MenuItem>
                  <MenuItem value="oldest">En Eski</MenuItem>
                  <MenuItem value="highest">En Yüksek Emisyon</MenuItem>
                  <MenuItem value="lowest">En Düşük Emisyon</MenuItem>
                  <MenuItem value="title">Başlığa Göre</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={1}>
              <Tooltip title="Filtreleri Temizle">
                <IconButton 
                  onClick={() => {
                    setSearchTerm('');
                    setFilterPeriod('all');
                    setFilterSeason('all');
                    setDateRange('all');
                    setSortBy('newest');
                    setCurrentPage(1);
                  }}
                >
                  🔍
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>

          {/* Toplu İşlemler */}
          {selectedCalculations.length > 0 && (
            <Box mt={2} display="flex" gap={1} alignItems="center">
              <Typography variant="body2" color="textSecondary">
                {selectedCalculations.length} hesaplama seçildi:
              </Typography>
              <Button 
                size="small" 
                onClick={() => handleBulkAction('compare')}
              >
                ⚖️ Karşılaştır
              </Button>
              <Button 
                size="small" 
                onClick={() => handleBulkAction('export')}
              >
                📊 Dışa Aktar
              </Button>
              <Button 
                size="small" 
                color="error"
                onClick={() => handleBulkAction('delete')}
              >
                🗑️ Sil
              </Button>
            </Box>
          )}
        </CardContent>
      </FilterCard>

      {/* Sonuçlar Özeti */}
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        {filteredCalculations.length} hesaplama bulundu
      </Typography>

      {/* Hesaplama Listesi */}
      <Grid container spacing={2}>
        {paginatedCalculations.map((calculation) => (
          <Grid item xs={12} key={calculation.id}>
            <StyledCard 
              onClick={() => onLoad(calculation)}
              sx={{ 
                border: selectedCalculations.includes(calculation.id) ? 
                  '2px solid #1976d2' : '1px solid transparent' 
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="h6" noWrap>
                        {calculation.title || 'Başlıksız Hesaplama'}
                      </Typography>
                      <EmissionChip
                        label={`📈 ${getEmissionLabel(calculation.total)}`}
                        color={getEmissionSeverity(calculation.total)}
                        size="small"
                        severity={getEmissionSeverity(calculation.total)}
                      />
                    </Box>

                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        🏨
                        <Typography variant="body2" color="textSecondary">
                          {calculation.hotelName || 'Bilinmeyen Otel'}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        📅
                        <Typography variant="body2" color="textSecondary">
                          {format(parseISO(calculation.timestamp), 'dd MMM yyyy, HH:mm', { locale: tr })}
                        </Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="textSecondary">
                          Toplam Emisyon
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {Math.round(calculation.total)} kg CO₂e
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="textSecondary">
                          Oda-Gece Başına
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {Math.round(calculation.perRoomNight)} kg
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="textSecondary">
                          Periyot
                        </Typography>
                        <Typography variant="body2">
                          {getPeriodLabel(calculation.period)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="textSecondary">
                          Mevsim
                        </Typography>
                        <Typography variant="body2">
                          {getSeasonLabel(calculation.season)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, calculation)}
                      size="small"
                    >
                      ⋮
                    </IconButton>
                    
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCalculationSelect(calculation.id);
                      }}
                    >
                      {selectedCalculations.includes(calculation.id) ? 'Seçildi' : 'Seç'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      {/* Sayfalama */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* Menü */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuList>
          <MuiMenuItem onClick={() => {
            onLoad(activeCalculation);
            handleMenuClose();
          }}>
            <ListItemIcon>👁️</ListItemIcon>
            <ListItemText>Görüntüle</ListItemText>
          </MuiMenuItem>
          
          <MuiMenuItem onClick={() => {
            onAddToComparison(activeCalculation);
            handleMenuClose();
          }}>
            <ListItemIcon>⚖️</ListItemIcon>
            <ListItemText>Karşılaştırmaya Ekle</ListItemText>
          </MuiMenuItem>
          
          <MuiMenuItem onClick={() => {
            onExport([activeCalculation.id], 'pdf');
            handleMenuClose();
          }}>
            <ListItemIcon>📄</ListItemIcon>
            <ListItemText>PDF İndir</ListItemText>
          </MuiMenuItem>
          
          <MuiMenuItem 
            onClick={() => {
              setDeleteDialog({ open: true, calculation: activeCalculation });
              handleMenuClose();
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>🗑️</ListItemIcon>
            <ListItemText>Sil</ListItemText>
          </MuiMenuItem>
        </MenuList>
      </Menu>

      {/* Silme Onay Dialogu */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, calculation: null })}>
        <DialogTitle>Hesaplamayı Sil</DialogTitle>
        <DialogContent>
          <Typography>
            "{deleteDialog.calculation?.title}" adlı hesaplamayı silmek istediğinizden emin misiniz?
            Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, calculation: null })}>
            İptal
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TourismCalculationHistory;
